// Starter Tape sandbox — /starter-tape/sandbox. The free studio that opens
// once Side A's tape 3 is done (it grew out of the Phase 1 sound check). It
// grows with Side A: drums after Pulse, tempo too; bass after Low End; chord
// blocks and a choice of scale after Two Moods. The working loop is kept as
// a draft, and it can be saved to the Tape Shelf like any tape.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "../lib/utils";
import {
  SCALES,
  clearTrack,
  demoSong,
  ensureTracks,
  setBarChord,
  setScale,
} from "../lib/starterTape";
import ChordStrip from "../components/starterTape/ChordStrip";
import {
  isSandboxUnlocked,
  nextTakeTitle,
  saveTake,
} from "../lib/starterTapeProgress";
import Studio from "../components/starterTape/Studio";
import TapeShell from "../components/starterTape/TapeShell";
import useTapePlayer from "../components/starterTape/useTapePlayer";
import {
  loadDraft,
  loadProgress,
  saveDraft,
  saveProgress,
} from "../components/starterTape/storage";

const extraButton =
  "rounded-lg border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800";

// Lanes the sandbox offers, and the tape that unlocks each (null = always).
const LANES = [
  { id: "pad", instrument: "pad", unlockedBy: null },
  { id: "bell", instrument: "bell", unlockedBy: null },
  { id: "bass", instrument: "bass", unlockedBy: "lowEnd" },
  { id: "drums", instrument: "drums", unlockedBy: "pulse" },
];

// Chords offered in the sandbox strip: every chord of the minor scale except
// the diminished ii°.
const SANDBOX_CHORDS = [0, 2, 3, 4, 5, 6];

const unlockedLanes = (progress) =>
  LANES.filter((l) => !l.unlockedBy || progress.completed[l.unlockedBy]).map(
    ({ id, instrument }) => ({ id, instrument })
  );

export default function StarterTapeSandbox() {
  const [progress, setProgress] = useState(loadProgress);
  const [song, setSong] = useState(() =>
    ensureTracks(loadDraft("sandbox") || demoSong(), unlockedLanes(progress))
  );
  const hasTempo = !!progress.completed.pulse;
  const hasChords = !!progress.completed.twoMoods;
  const [savedTitle, setSavedTitle] = useState(null);
  const player = useTapePlayer(song);

  useEffect(() => saveDraft("sandbox", song), [song]);
  useEffect(() => saveProgress(progress), [progress]);

  if (!isSandboxUnlocked(progress)) {
    return (
      <TapeShell
        title="Sandbox"
        subtitle="A free studio with everything you've unlocked."
      >
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-6 text-zinc-300">
          <p>The sandbox opens when you finish tape 3, Home.</p>
          <Link
            to="/starter-tape"
            className="mt-4 inline-block rounded-lg bg-amber-300 px-4 py-2 font-semibold text-zinc-900 hover:bg-amber-200"
          >
            Back to the tapes
          </Link>
        </div>
      </TapeShell>
    );
  }

  const edit = (s) => {
    setSong(s);
    setSavedTitle(null);
  };

  const saveToShelf = () => {
    const title = nextTakeTitle(progress, null);
    setProgress(
      saveTake(progress, {
        id: `take-${Date.now()}`,
        tapeId: null,
        title,
        song,
        met: true,
        savedAt: Date.now(),
      })
    );
    setSavedTitle(title);
  };

  return (
    <TapeShell
      title="Sandbox"
      subtitle={
        song.scale === "minorPentatonic"
          ? "No brief, no rules beyond the scale. Every note here belongs to A minor pentatonic, so nothing you place can sound wrong."
          : "No brief. You're in A natural minor: all seven notes, and chord blocks for the pad."
      }
    >
      <Studio
        song={song}
        setSong={edit}
        player={player}
        showTempo={hasTempo}
        beforeLanes={
          hasChords ? (
            <>
              <div
                className="mb-4 flex flex-wrap items-center gap-2 text-sm"
                role="group"
                aria-label="Scale"
              >
                <span className="text-zinc-500">Scale</span>
                {["minorPentatonic", "naturalMinor"].map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => edit(setScale(song, id))}
                    aria-pressed={song.scale === id}
                    className={cn(
                      "rounded-md px-3 py-1",
                      song.scale === id
                        ? "bg-zinc-100 text-zinc-900"
                        : "border border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                    )}
                  >
                    A {SCALES[id].name.toLowerCase()}
                  </button>
                ))}
                {song.scale === "minorPentatonic" && (
                  <span className="text-xs text-zinc-500">
                    Switch to natural minor for chord blocks.
                  </span>
                )}
              </div>
              {song.scale === "naturalMinor" && (
                <ChordStrip
                  song={song}
                  trackId="pad"
                  chords={SANDBOX_CHORDS}
                  onPick={(bar, index) =>
                    edit(setBarChord(song, "pad", bar, index))
                  }
                />
              )}
            </>
          ) : null
        }
        transportExtras={
          <>
            <button
              type="button"
              onClick={() =>
                edit(song.tracks.reduce((s, t) => clearTrack(s, t.id), song))
              }
              className={extraButton}
            >
              Clear all
            </button>
            <button
              type="button"
              onClick={() =>
                edit(ensureTracks(demoSong(), unlockedLanes(progress)))
              }
              className={extraButton}
            >
              Demo loop
            </button>
            <button
              type="button"
              onClick={saveToShelf}
              className="rounded-lg bg-zinc-100 px-3 py-2 text-sm font-semibold text-zinc-900 hover:bg-white"
            >
              Save to shelf
            </button>
          </>
        }
      />
      {savedTitle && (
        <p className="mt-4 text-sm text-emerald-300">
          Saved “{savedTitle}” to your Tape Shelf.
        </p>
      )}
    </TapeShell>
  );
}
